import { useMemo } from "react";
import { Button, Grid, Header, Icon, Label, Segment, Statistic, Table } from "semantic-ui-react";
import { roomLabel, STATUSES_HOLDING_ROOM } from "./adminHelpers";

const STATUS_LABEL_COLOR = {
  Booked: "yellow",
  Vacant: "green",
  Maintenance: "grey",
};

function RoomStatusBadge({ status }) {
  return <Label color={STATUS_LABEL_COLOR[status] || "grey"}>{status}</Label>;
}

export default function RoomOverviewPage({ gh, rooms, bookings, onBack, onManage }) {
  const today = new Date().toISOString().slice(0, 10);

  const bookedRoomIds = useMemo(
    () =>
      new Set(
        bookings
          .filter(
            (b) =>
              b.guestHouseId === gh.id &&
              STATUSES_HOLDING_ROOM.includes(b.status) &&
              b.checkOut >= today,
          )
          /* a booking can hold more than one room — count every assigned
             room, not just the first (b.roomId) */
          .flatMap((b) =>
            (b.roomAssignments || []).length
              ? b.roomAssignments.map((a) => a.roomId)
              : [b.roomId],
          ),
      ),
    [bookings, gh.id, today],
  );

  const ghRooms = useMemo(
    () =>
      rooms
        .filter((r) => r.guestHouseId === gh.id)
        .map((r) => ({
          ...r,
          displayStatus:
            r.status === "maintenance"
              ? "Maintenance"
              : bookedRoomIds.has(r.id)
                ? "Booked"
                : "Vacant",
        }))
        .sort((a, b) =>
          a.roomNumber.localeCompare(b.roomNumber, undefined, {
            numeric: true,
          }),
        ),
    [rooms, gh.id, bookedRoomIds],
  );

  const vacant = ghRooms.filter((r) => r.displayStatus === "Vacant").length;
  const booked = ghRooms.filter((r) => r.displayStatus === "Booked").length;
  const maint = ghRooms.filter((r) => r.displayStatus === "Maintenance").length;

  return (
    <>
      <Segment basic vertical>
        <Grid stackable verticalAlign="middle">
          <Grid.Column width={1}>
            <Button icon circular basic onClick={onBack} title="Back" aria-label="Back">
              <Icon name="arrow left" />
            </Button>
          </Grid.Column>
          <Grid.Column width={9}>
            <Header as="h2" icon="building outline" content={gh.name} subheader="Room Overview" />
          </Grid.Column>
          <Grid.Column width={6} textAlign="right">
            <Statistic.Group size="mini" widths={maint > 0 ? 4 : 3}>
              <Statistic>
                <Statistic.Value>{ghRooms.length}</Statistic.Value>
                <Statistic.Label>Total</Statistic.Label>
              </Statistic>
              <Statistic color="green">
                <Statistic.Value>{vacant}</Statistic.Value>
                <Statistic.Label>Vacant</Statistic.Label>
              </Statistic>
              <Statistic color="yellow">
                <Statistic.Value>{booked}</Statistic.Value>
                <Statistic.Label>Booked</Statistic.Label>
              </Statistic>
              {maint > 0 && (
                <Statistic color="grey">
                  <Statistic.Value>{maint}</Statistic.Value>
                  <Statistic.Label>Maintenance</Statistic.Label>
                </Statistic>
              )}
            </Statistic.Group>
          </Grid.Column>
        </Grid>
      </Segment>

      <Segment>
        <Table celled selectable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Room No.</Table.HeaderCell>
              <Table.HeaderCell>Room Type</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Capacity</Table.HeaderCell>
              <Table.HeaderCell>Action</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {ghRooms.map((r) => (
              <Table.Row key={r.id}>
                <Table.Cell>
                  <strong>{roomLabel(r.roomNumber, gh?.shortName)}</strong>
                </Table.Cell>
                <Table.Cell>{r.roomType}</Table.Cell>
                <Table.Cell>
                  <RoomStatusBadge status={r.displayStatus} />
                </Table.Cell>
                <Table.Cell>
                  {r.capacity} guest{r.capacity !== 1 ? "s" : ""}
                </Table.Cell>
                <Table.Cell>
                  <Button size="small" basic color="blue" onClick={onManage}>
                    <Icon name="setting" />
                    Manage
                  </Button>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </Segment>
    </>
  );
}
